import React, { useEffect, useRef, useState } from "react";
import { compressImage, resolveBucketImageSrc } from "../../../utils/utils";
import { useSelector } from "react-redux";
import Icon from "../icons/Icon";
import iconsList from "../icons/iconsList";
import ImagePreview from "../imagePreview/ImagePreview";
import ImageUploadRequirementsBanner from "./imageUploadRequirementsBanner/ImageUploadRequirementsBanner";
import classes from "./ImageUpload.module.css";

export const isImageOverMaxSize = (imageFile, maxSizeBytes) =>
    imageFile instanceof File && maxSizeBytes != null && imageFile.size > maxSizeBytes;

const ImageUpload = ({
    onRemove,
    imageFile,
    onImageChange,
    removeAriaLabel,
    maxSizeBytes,
    bannerKey,
    size,
}) => {
    const inputRef = useRef(null);
    const [objectUrl, setObjectUrl] = useState(null);
    const [hasError, setHasError] = useState(false);
    const { texts } = useSelector((state) => state.translate);

    useEffect(() => {
        if (imageFile instanceof File) {
            const url = URL.createObjectURL(imageFile);
            setObjectUrl(url);
            return () => URL.revokeObjectURL(url);
        }
        setObjectUrl(null);
        return undefined;
    }, [imageFile]);

    const previewSrc = imageFile instanceof File
        ? objectUrl
        : typeof imageFile === "string"
        ? resolveBucketImageSrc(imageFile)
        : null;

    const openPicker = () => inputRef.current?.click();

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.match(/^image\/(png|jpeg|gif|webp)$/i) || isImageOverMaxSize(file, maxSizeBytes)) {
            setHasError(true);
            return;
        }
        setHasError(false);
        try {
            const compressedFile = await compressImage(file, 800, 800, 0.5);
            onImageChange(compressedFile);
        } catch (error) {
            console.error("Image compression failed:", error);
            setHasError(true);
        }
    };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        await handleFile(file);
    };

    const clearImage = (e) => {
        e?.stopPropagation?.();
        setHasError(false);
        onImageChange(null);
    };

    const onDropZoneKeyDown = (e) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openPicker();
        }
    };

    return (
        <div className={classes.Wrap}>
            <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                className={classes.HiddenInput}
                onChange={onFile}
            />

            <div
                className={classes.UploadArea}
                style={size ? { width: size, height: size } : undefined}
            >
                {previewSrc ?
                    <ImagePreview
                        src={previewSrc}
                        alt=""
                        onRemove={onRemove ?? clearImage}
                        removeAriaLabel={removeAriaLabel}
                    />
                :
                    <button
                        type="button"
                        className={classes.DropZone}
                        onClick={openPicker}
                        onKeyDown={onDropZoneKeyDown}
                    >
                        <div className={classes.IconCircle}>
                            <Icon name={iconsList.addImg} size={32} />
                        </div>
                        <span className={classes.UploadLink}>{texts.uploadImageAction}</span>
                    </button>
                }
            </div>

            {maxSizeBytes ?
                <ImageUploadRequirementsBanner
                    isError={hasError}
                    maxSizeBytes={maxSizeBytes}
                    bannerKey={bannerKey}
                />
            : null}
        </div>
    );
};

export default ImageUpload;
